import { GripVertical, X, AlertTriangle } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useChatbot } from '../../../contexts/ChatbotContext';
import { useMiniApps } from '../../../hooks/useMiniApps';
import { useOrderTracking } from '../../../hooks/useOrderTracking';
import { MINI_APP_ICONS, type MiniAppId } from '../../../utils/miniAppsIcons';
import { Switch } from '../../ui/Switch';
import { useDesignColors } from '../../../hooks/useDesignColors';
import { supabase } from '../../../lib/supabase';

export function MiniAppToggle() {
  const { chatbotPublicId } = useChatbot();
  const { 
    miniApps, 
    toggleMiniApp, 
    toggleButtonVisibility,
    updateMiniAppLabel, 
    reorderMiniApps, 
    loading, 
    error, 
    initialized, 
    initializeMiniApps 
  } = useMiniApps();
  const { 
    settings: orderTrackingSettings, 
    initialized: orderTrackingInitialized,
    updateButtonVisibility,
    setButtonVisibility,
    loading: orderTrackingLoading 
  } = useOrderTracking();
  const { colors } = useDesignColors();
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [inputValues, setInputValues] = useState<Record<string, string>>({});
  const [showConfirmDisable, setShowConfirmDisable] = useState<MiniAppId | null>(null);
  const [orderTrackingButtonVisible, setOrderTrackingButtonVisible] = useState<boolean>(true);

  // Initialize mini apps when component mounts
  useEffect(() => {
    if (chatbotPublicId) {
      initializeMiniApps(chatbotPublicId);
    }
  }, [chatbotPublicId, initializeMiniApps]);

  // Initialize input values from miniApps
  useEffect(() => {
    const values = miniApps.reduce((acc, app) => ({
      ...acc,
      [app.id]: app.label
    }), {});
    setInputValues(values);
  }, [miniApps]);

  // Initialize order tracking button visibility
  useEffect(() => {
    if (orderTrackingSettings) {
      console.log("Setting initial order tracking button visibility:", orderTrackingSettings.buttonVisible);
      // Only set to false if explicitly false
      setOrderTrackingButtonVisible(orderTrackingSettings.buttonVisible !== false);
    }
  }, [orderTrackingSettings]);

  if (!chatbotPublicId) return null;

  // Filter to only show enabled mini apps
  const enabledMiniApps = miniApps.filter(app => app.enabled);

  // Check if order tracking is enabled (used for display message when no apps are enabled)
  const hasEnabledApps = enabledMiniApps.length > 0;

  const handleDragStart = (index: number) => {
    console.log('Drag start:', index);
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    console.log('Drag over:', index);
    
    // Create a copy of the mini apps array
    const newMiniApps = [...miniApps];
    const draggedApp = newMiniApps[draggedIndex];
    
    // Remove the dragged app from its original position
    newMiniApps.splice(draggedIndex, 1);
    
    // Insert it at the new position
    newMiniApps.splice(index, 0, draggedApp);

    // Update the state and the database
    setDraggedIndex(index);
    reorderMiniApps(newMiniApps, chatbotPublicId);
  };

  const handleDragEnd = () => {
    console.log('Drag end');
    setDraggedIndex(null);
  };
  
  const handleDrop = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    console.log('Drop at:', index);
    
    if (draggedIndex === null) return;
    
    // This is to ensure the drop is properly handled
    // Sometimes react's drag and drop can be finicky
    const newMiniApps = [...miniApps];
    const draggedApp = newMiniApps[draggedIndex];
    newMiniApps.splice(draggedIndex, 1);
    newMiniApps.splice(index, 0, draggedApp);
    
    reorderMiniApps(newMiniApps, chatbotPublicId);
    setDraggedIndex(null);
  };

  const handleInputChange = (id: MiniAppId, value: string) => {
    // Update local state immediately
    setInputValues(prev => ({
      ...prev,
      [id]: value
    }));
  };

  const handleInputBlur = (id: MiniAppId, value: string) => {
    // Only update if value has changed
    if (value !== miniApps.find(app => app.id === id)?.label) {
      updateMiniAppLabel(id, value, chatbotPublicId);
    }
  };
  
  const handleDisableClick = (id: MiniAppId) => {
    setShowConfirmDisable(id);
  };
  
  const handleConfirmDisable = (id: MiniAppId) => {
    if (chatbotPublicId) {
      toggleMiniApp(id, chatbotPublicId);
      setShowConfirmDisable(null);
    }
  };

  const toggleOrderTrackingButtonVisibility = async () => {
    if (!chatbotPublicId || !orderTrackingSettings) return;
    
    // Get the new visibility state (opposite of current state)
    const newVisibility = !orderTrackingButtonVisible;
    
    // Update UI immediately for better UX
    setOrderTrackingButtonVisible(newVisibility);
    
    console.log('Setting Order Tracking button visibility to:', newVisibility);
    
    // Call the hook function to update the database
    // If newVisibility is false, explicitly set false, otherwise set to true
    const success = await setButtonVisibility(newVisibility);
    
    // If failed, revert the UI
    if (!success) {
      console.error('Failed to update Order Tracking button visibility');
      setOrderTrackingButtonVisible(!newVisibility);
    } else {
      console.log('Successfully updated Order Tracking button visibility to:', newVisibility);
    }
  };

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-500/10 text-red-400 p-4 rounded-lg">
          {error instanceof Error ? error.message : String(error)}
        </div>
      )}

      {!hasEnabledApps ? (
        <div className="text-center py-8 mb-4 bg-white/5 rounded-xl p-6 border border-white/10">
          <p className="text-gray-400 mb-4">No mini apps enabled yet.</p>
          <p className="text-gray-500 text-sm">Add mini apps using the button above to configure them here.</p>
        </div>
      ) : (
        <>
          {/* All Enabled Mini Apps (including Order Tracking) */}
          {enabledMiniApps.map((app, index) => {
            const Icon = app.id === 'order-tracking' 
              ? () => (
                <svg 
                  className="w-5 h-5" 
                  style={{ color: colors.gradientStart }} 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24" 
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                </svg>
              )
              : MINI_APP_ICONS[app.id as MiniAppId];
            
            const isConfirmingDisable = showConfirmDisable === app.id;
            const isOrderTracking = app.id === 'order-tracking';
            
            return (
              <div
                key={app.id}
                draggable={!isConfirmingDisable}
                onDragStart={() => handleDragStart(index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragEnd={handleDragEnd}
                onDrop={(e) => handleDrop(e, index)}
                className="bg-white/5 backdrop-blur-xl rounded-xl p-6 border border-white/10 cursor-move relative"
              >
                {isConfirmingDisable && (
                  <div className="absolute inset-0 bg-black/70 backdrop-blur-sm rounded-xl flex items-center justify-center z-10">
                    <div className="bg-gray-800 p-6 rounded-xl max-w-md text-center">
                      <AlertTriangle className="w-10 h-10 text-yellow-500 mx-auto mb-4" />
                      <h4 className="text-lg font-semibold text-white mb-2">Disable Mini App?</h4>
                      <p className="text-gray-300 mb-6">
                        This will remove the mini app from your chatbot and hide all related settings. You can re-enable it later from the Add Mini App screen.
                      </p>
                      <div className="flex gap-3 justify-center">
                        <button 
                          onClick={() => setShowConfirmDisable(null)}
                          className="px-4 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-colors"
                        >
                          Cancel
                        </button>
                        <button 
                          onClick={() => handleConfirmDisable(app.id as MiniAppId)}
                          className="px-4 py-2 bg-red-500/80 hover:bg-red-500 text-white rounded-lg transition-colors"
                        >
                          Disable
                        </button>
                      </div>
                    </div>
                  </div>
                )}
                
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <GripVertical className="w-5 h-5 text-gray-500" />
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center"
                      style={{
                        background: `linear-gradient(135deg, ${colors.gradientStart}20, ${colors.gradientEnd}20)`,
                      }}
                    >
                      {isOrderTracking ? (
                        <svg 
                          className="w-5 h-5" 
                          style={{ color: colors.gradientStart }} 
                          fill="none" 
                          stroke="currentColor" 
                          viewBox="0 0 24 24" 
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                        </svg>
                      ) : (
                        <Icon className="w-5 h-5" style={{ color: colors.gradientStart }} />
                      )}
                    </div>
                    <h3 className="text-lg font-semibold text-white">{app.title}</h3>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-400">Show Button</span>
                      {isOrderTracking ? (
                        <Switch
                          checked={orderTrackingButtonVisible}
                          onChange={toggleOrderTrackingButtonVisibility}
                          disabled={orderTrackingLoading}
                        />
                      ) : (
                        <Switch
                          checked={app.buttonVisible !== false}
                          onChange={() => toggleButtonVisibility(app.id as MiniAppId, chatbotPublicId)}
                          disabled={loading}
                        />
                      )}
                    </div>
                    <button
                      onClick={() => handleDisableClick(app.id as MiniAppId)}
                      className="p-2 rounded-full text-gray-400 hover:text-red-400 hover:bg-red-400/10 transition-colors"
                      title="Disable Mini App"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                </div>
                <div className="mt-4">
                  <label className="block text-sm text-gray-400 mb-2">Button Label</label>
                  {isOrderTracking ? (
                    <>
                      <input
                        type="text"
                        value={orderTrackingSettings?.quick_action_text || "Track my order"}
                        disabled
                        className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white focus:outline-none"
                        placeholder="Button Label"
                      />
                      <p className="mt-2 text-xs text-gray-500">
                        To change the button label, navigate to the Order Tracking settings page.
                      </p>
                    </>
                  ) : (
                    <input
                      type="text"
                      value={inputValues[app.id] || ''}
                      onChange={(e) => handleInputChange(app.id as MiniAppId, e.target.value)}
                      onBlur={(e) => handleInputBlur(app.id as MiniAppId, e.target.value)}
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      placeholder="Button Label"
                    />
                  )}
                </div>
              </div>
            );
          })}
        </>
      )}
    </div>
  );
}